/**
 * Pure helpers behind "Scheduled run" (TRA-1475), kept out of the components
 * so they are testable without a DOM. The api checks every rule here again,
 * then checks the run itself: the dataset, the preset and the adapter a run
 * would start from.
 */

import { z } from 'zod'

import type {
  TrainingLaunchView,
  TrainingScheduleUpdate,
  TrainingScheduleUpdateWeekdaysItem,
  TrainingScheduleView,
} from '@/api/generated/model'

import { GRPO_STEPS, LAUNCH_STATUS_LABEL, grpoSteps } from './launch'

export type Weekday = TrainingScheduleUpdateWeekdaysItem

/** Monday first, the order the api keeps them in. */
export const WEEKDAYS: { id: Weekday; label: string }[] = [
  { id: 'mon', label: 'Mon' },
  { id: 'tue', label: 'Tue' },
  { id: 'wed', label: 'Wed' },
  { id: 'thu', label: 'Thu' },
  { id: 'fri', label: 'Fri' },
  { id: 'sat', label: 'Sat' },
  { id: 'sun', label: 'Sun' },
]

// The api's patterns (schemas/training_run.py): LOCAL_TIME_PATTERN,
// TIME_ZONE_PATTERN, PRESET_NAME_PATTERN and CHECKPOINT_PATH_PATTERN.
const LOCAL_TIME = /^(?:[01]\d|2[0-3]):[0-5]\d$/
const TIME_ZONE = /^[A-Za-z_]+(?:\/[A-Za-z0-9_+-]+)+$/
const PRESET = /^[a-z][a-z0-9]*(?:[.-][a-z0-9]+)*$/
const CHECKPOINT = /^[A-Za-z0-9_-][A-Za-z0-9._-]*(?:\/[A-Za-z0-9_-][A-Za-z0-9._-]*)*\/?$/

const TIME_ZONE_MESSAGE = 'Enter an IANA time zone such as America/Los_Angeles.'

/** Whether the browser knows the zone. The api asks its own database too. */
function isTimeZone(name: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: name })
    return true
  } catch {
    return false
  }
}

/** The form as its inputs hold it, before validation. */
export interface ScheduleDraft {
  weekdays: Weekday[]
  localTime: string
  timeZone: string
  /** The pinned dataset, or null for the team's newest proven one. */
  dataset: string | null
  validationIds: string[]
  /** The pinned starting checkpoint; empty for the team's best prior adapter. */
  checkpoint: string
  preset: string
  stepsText: string
  enabled: boolean
}

export const scheduleSchema = z.object({
  weekdays: z.array(z.string()).min(1, 'Pick at least one day.'),
  localTime: z.string().regex(LOCAL_TIME, 'Enter a time.'),
  timeZone: z.string().max(64, TIME_ZONE_MESSAGE).regex(TIME_ZONE, TIME_ZONE_MESSAGE).refine(isTimeZone, TIME_ZONE_MESSAGE),
  checkpoint: z
    .string()
    .max(512, 'Enter a shorter folder path.')
    .refine((path) => path === '' || CHECKPOINT.test(path), 'Enter a folder in the bucket, or leave this empty.'),
  preset: z
    .string()
    .max(64, 'Enter a shorter preset name.')
    .regex(PRESET, 'Choose a preset.'),
  stepsText: z
    .string()
    .refine(
      (text) => grpoSteps(text) !== null,
      `Enter a whole number from ${String(GRPO_STEPS.min)} to ${String(GRPO_STEPS.max)}.`,
    ),
})

export type ScheduleField = keyof z.infer<typeof scheduleSchema>

export type ScheduleErrors = Partial<Record<ScheduleField, string>>

/** The request a save sends, or each field's first problem. */
export type ParsedSchedule =
  | { kind: 'ok'; update: TrainingScheduleUpdate }
  | { kind: 'error'; errors: ScheduleErrors }

function isField(value: unknown): value is ScheduleField {
  return typeof value === 'string' && value in scheduleSchema.shape
}

/** The draft's days in week order, which is how the api stores them. */
function inWeekOrder(days: Weekday[]): Weekday[] {
  return WEEKDAYS.map((day) => day.id).filter((id) => days.includes(id))
}

export function parseSchedule(draft: ScheduleDraft): ParsedSchedule {
  const result = scheduleSchema.safeParse({ ...draft, checkpoint: draft.checkpoint.trim(), preset: draft.preset.trim() })
  const steps = grpoSteps(draft.stepsText)
  if (!result.success || steps === null) {
    const errors: ScheduleErrors = {}
    for (const issue of result.error?.issues ?? []) {
      const field = issue.path[0]
      if (isField(field) && !(field in errors)) errors[field] = issue.message
    }
    return { kind: 'error', errors }
  }
  return {
    kind: 'ok',
    update: {
      weekdays: inWeekOrder(draft.weekdays),
      local_time: draft.localTime,
      time_zone: draft.timeZone,
      preset: result.data.preset,
      max_steps: steps,
      dataset: draft.dataset,
      initial_checkpoint: result.data.checkpoint === '' ? null : result.data.checkpoint,
      validation_datasets: draft.validationIds,
      enabled: draft.enabled,
    },
  }
}

/** What a team's first schedule starts from. */
export interface ScheduleDefaults {
  preset: string
  /** The browser's own zone, so a time typed here means the wall clock. */
  timeZone: string
}

function draftOfSaved(saved: TrainingScheduleView): ScheduleDraft {
  return {
    weekdays: saved.weekdays,
    localTime: saved.local_time,
    timeZone: saved.time_zone,
    dataset: saved.dataset ?? null,
    validationIds: saved.validation_datasets ?? [],
    checkpoint: saved.initial_checkpoint ?? '',
    preset: saved.preset,
    stepsText: String(saved.max_steps),
    enabled: saved.enabled,
  }
}

/** The saved schedule as a draft, or an empty one a first save fills in. */
export function scheduleDraft(saved: TrainingScheduleView | null, defaults: ScheduleDefaults): ScheduleDraft {
  if (saved !== null) return draftOfSaved(saved)
  return {
    weekdays: [],
    localTime: '10:00',
    timeZone: defaults.timeZone,
    dataset: null,
    validationIds: [],
    checkpoint: '',
    preset: defaults.preset,
    stepsText: String(GRPO_STEPS.default),
    enabled: true,
  }
}

/** Whether a save of the draft would change what is saved. A team with no schedule always has one to save. */
export function isScheduleChanged(draft: ScheduleDraft, saved: TrainingScheduleView | null): boolean {
  if (saved === null) return true
  const canonical = (value: ScheduleDraft): string =>
    JSON.stringify({
      ...value,
      weekdays: inWeekOrder(value.weekdays),
      validationIds: [...value.validationIds].sort(),
      checkpoint: value.checkpoint.trim(),
      preset: value.preset.trim(),
      stepsText: String(Number(value.stepsText)),
    })
  return canonical(draft) !== canonical(draftOfSaved(saved))
}

/** The browser's time zone, the default for a team's first schedule. */
export function browserTimeZone(): string {
  return new Intl.DateTimeFormat().resolvedOptions().timeZone
}

/** "Tue, Oct 20, 10:00 AM PDT": a tick in the schedule's own zone. */
export function formatTick(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
    timeZoneName: 'short',
  }).format(new Date(iso))
}

/** The "Next run" line of a saved schedule. */
export function nextRunLine(saved: TrainingScheduleView): string {
  if (saved.next_tick_at === null) return 'Paused'
  return formatTick(saved.next_tick_at, saved.time_zone)
}

/** The "Last run" line: the newest run the schedule started. */
export function lastRunLine(run: TrainingLaunchView | null): string {
  if (run === null) return 'None yet'
  return `${run.run_id} · ${LAUNCH_STATUS_LABEL[run.status]}`
}

/** What the api says of the team's registry (TrainingScheduleResponse). */
export interface NewestDataset {
  /** The team's newest proven dataset, or null when there is none to name. */
  id: string | null
  /** Why the registry cannot name it, when it cannot. */
  problem: string | null
}

/** Under the dataset picker: what a run will train, in the words of the choice made. */
export function datasetNote(
  dataset: string | null,
  { id: newest, problem }: NewestDataset,
): { text: string; missing: boolean } {
  if (dataset !== null) {
    return { text: 'Every run trains this dataset until you change it.', missing: false }
  }
  if (problem !== null) {
    return {
      text: `The registry cannot name this team's newest proven dataset. Pin a dataset until that is fixed. The api says: ${problem}.`,
      missing: true,
    }
  }
  if (newest === null) {
    return {
      text: 'This team has no proven dataset yet. Pin a dataset, or build one from the Jobs page first.',
      missing: true,
    }
  }
  return {
    text: `Each run trains the team's newest proven dataset. Today that is ${newest}.`,
    missing: false,
  }
}
